/**
 * AktenPage — Akten-Übersicht mit Tabelle + Kanban + Suche + Filter + Statistik
 * DESIGN: Micro-minimalistisch, Betriebsfarben-Akzente, professionell
 * Besser als WinValue: Kanban-Board, Echtzeit-Suche, Dringlichkeits-Markierung
 */

import { useApp } from '@/contexts/AppContext';
import { useAktenPaginated, useAktenStats, useAktenFristenMin, type AktenSubstatusFilter } from '@/hooks/useSupabase';
import { STATUS_LABELS, STATUS_COLORS, PRIORITAET_COLORS, type BusinessUnit, type AktenStatus, supabase } from '@/lib/supabase';
import {
  STELLUNGNAHME_STATUS_FILTER_OPTIONS,
  istStellungnahmeStatusFilterAktiv,
  stellungnahmeStatusLabel,
  stellungnahmeStatusChipClasses,
  berechneSchriftsatzfristBadge,
  schriftsatzfristBadgeClasses,
  schriftsatzfristBadgeText,
} from '@/lib/akten-stellungnahme';
import { pruefungStatusLabel, pruefungStatusBadgeClasses } from '@/lib/akten-pruefung';
import { bereinigNeueAkteParam } from '@/lib/akten-neue-akte-url-cleanup';
import { PruefungBadge } from '@/components/akte/PruefungBadge';
import { ErstellerBadge } from '@/components/akte/ErstellerBadge';
import {
  HAUPTPHASEN_LABELS,
  zahlungsstatusLabel,
  zahlungsstatusBadgeClasses,
  klageStatusLabel,
  klageStatusBadgeClasses,
} from '@/lib/akten-hauptphase';
import { BETRIEBE, FEATURE_STATUS, type BetriebId } from '@/lib/betriebe';
import type { Akte } from '@/lib/database.types';
import { useLocation, useParams } from 'wouter';
import React, { Component, useState, useMemo, useCallback, useEffect, useRef } from 'react';
import ForderungsmanagerDrawer from '@/components/ForderungsmanagerDrawer';
import QuickActionButtons from '@/components/QuickActionButtons';

// ─── QuickActionErrorBoundary ──────────────────────────────────────────────────
// Fängt Render-Fehler in QuickActionButtons ab und rendert null statt die
// gesamte Tabellen-Zeile / Kanban-Karte zu zerstören (Issue #923).
class QuickActionErrorBoundary extends Component<{ children: React.ReactNode; resetKey?: string }, { hasError: boolean }> {
  constructor(props: { children: React.ReactNode; resetKey?: string }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }
  componentDidUpdate(prevProps: { children: React.ReactNode; resetKey?: string }) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false });
    }
  }
  componentDidCatch(error: Error) {
    console.error('[QuickActionErrorBoundary] QuickActionButtons-Fehler abgefangen:', error);
  }
  render() {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}
import FristSetzenModal from '@/components/FristSetzenModal';
import {
  Search, Plus, FolderOpen, Clock, LayoutGrid, List, X, Loader2,
  FileText, Receipt, AlertTriangle, TrendingUp,
  Calendar, Users, Car, Key, Instagram, ChevronRight, Link2, Camera, ShieldAlert, ChevronDown,
  Download, ArrowUp, ArrowDown, ArrowUpDown, DollarSign, Timer, Flame, CalendarClock,
} from 'lucide-react';
import { downloadCsv as downloadAktenCsv, type AktenExportRow } from '@/lib/akten-export';
import {
  berechneRechnungOffenSeit,
  berechneFaelligSeit,
  berechneCountdownPill,
  getDefaultSort,
  applyQuickSort,
  toggleColumnSort,
  sortStateToAktenSortierung,
  type AktenSortState,
  type QuickSortKey,
} from '@/lib/akten-sort-columns';
import { ForderungStagePill } from '@/components/forderung/ForderungStagePill';
import { toast } from 'sonner';
import { erstelleMietwagenInsertData } from '@/lib/mietwagen';
import {
  generiereAktenNr,
  generiereAktenNummer,
  erstelleGutachtenInsertData,
  erstelleProblemfallAufgaben,
  isUniqueViolation,
} from '@/lib/akten';
import { useAktenThumbnails } from '@/lib/akten-thumbnails';
import { aktenIdentifier } from '@/lib/akten-link';
import {
  erweiterterFilterToUrlParams,
  erweiterterFilterFromUrlParams,
  erweiterterFilterToJson,
  erweiterterFilterFromJson,
  hatErweitertenFilter,
  BEZAHL_STATUS_OPTIONS,
  ALTER_BUCKET_OPTIONS,
  FAELLIG_SEIT_OPTIONS,
  MIT_FRIST_OPTIONS,
  CREATED_VIA_OPTIONS,
  DEFAULT_BETRAG_MAX,
  type AktenErweiterterFilter,
} from '@/lib/akten-filter-erweiterung';

// ============================================================================
// Typen & Konstanten
// ============================================================================

type ViewMode = 'tabelle' | 'kanban';

const AUFTRAGSTYP_LABELS: Record<string, string> = {
  haftpflicht: 'Haftpflicht',
  kasko: 'Kasko',
  kostenvoranschlag: 'KVA',
  kurzgutachten: 'Kurzgutachten',
  beweissicherung: 'Beweissicherung',
  oldtimer: 'Oldtimer',
  wertgutachten: 'Wertgutachten',
  technisch: 'Technisch',
};

const KANBAN_COLUMNS: { status: AktenStatus; label: string; color: string }[] = [
  { status: 'neu', label: 'Neu', color: '#6B7280' },
  { status: 'in_bearbeitung', label: 'In Bearbeitung', color: '#2563EB' },
  { status: 'warten', label: 'Gutachten erstellt', color: '#D97706' },
  { status: 'abgeschlossen', label: 'Abgeschlossen', color: '#059669' },
  { status: 'storniert', label: 'Archiviert', color: '#9CA3AF' },
];

const STATUS_FILTER_OPTIONS = [
  { value: 'alle', label: 'Alle' },
  { value: 'neu', label: 'Neu' },
  { value: 'in_bearbeitung', label: 'In Bearbeitung' },
  { value: 'warten', label: 'Wartend' },
  { value: 'abgeschlossen', label: 'Abgeschlossen' },
  { value: 'storniert', label: 'Storniert' },
];

const AUFTRAGSTYP_FILTER_OPTIONS = [
  { value: 'alle', label: 'Alle Typen' },
  { value: 'haftpflicht', label: 'Haftpflicht' },
  { value: 'kasko', label: 'Kasko' },
  { value: 'kostenvoranschlag', label: 'KVA' },
  { value: 'kurzgutachten', label: 'Kurzgutachten' },
  { value: 'beweissicherung', label: 'Beweissicherung' },
  { value: 'wertgutachten', label: 'Wertgutachten' },
];

// Sub-Status-Filter-Optionen (Issue #416)
const ZAHLUNGSSTATUS_OPTIONS = [
  { value: 'offen',            label: 'Offen' },
  { value: 'teilweise_bezahlt',label: 'Teilweise bezahlt' },
  { value: 'bezahlt',          label: 'Bezahlt' },
  { value: 'gekuerzt',         label: 'Gekürzt' },
  { value: 'ueberzahlt',       label: 'Überzahlt' },
];

const KLAGE_STATUS_OPTIONS = [
  { value: 'keine',                label: 'Keine' },
  { value: 'mahnbescheid_gestellt',label: 'Mahnbescheid' },
  { value: 'klage_erhoben',        label: 'Klage erhoben' },
  { value: 'beweisaufnahme',       label: 'Beweisaufnahme' },
  { value: 'urteil',               label: 'Urteil' },
  { value: 'berufung',             label: 'Berufung' },
  { value: 'rechtskraeftig',       label: 'Rechtskräftig' },
];

const WARTEN_AUF_OPTIONS = [
  { value: 'warten_auf_kunde',        label: 'Warten auf Kunde' },
  { value: 'warten_auf_werkstatt',    label: 'Warten auf Werkstatt' },
  { value: 'warten_auf_anwalt',       label: 'Warten auf Anwalt' },
  { value: 'warten_auf_versicherung', label: 'Warten auf Versicherung' },
];

// Stage-Pill-Klassen gleich wie ForderungStagePill (Issue #653)
const STAGE_PILL_CLASSES: Record<number, string> = {
  0: 'bg-gray-100 text-gray-600',
  1: 'bg-blue-100 text-blue-700',
  2: 'bg-blue-100 text-blue-700',
  3: 'bg-yellow-100 text-yellow-700',
  4: 'bg-orange-100 text-orange-700',
  5: 'bg-red-100 text-red-700',
  6: 'bg-red-100 text-red-700',
  7: 'bg-red-100 text-red-800',
  8: 'bg-red-200 text-red-900',
  9: 'bg-green-50 text-zinc-500',
};

const STAGE_LABELS: Record<number, string> = {
  0: 'Offen',
  1: 'Anschreiben',
  2: 'Erinnerung',
  3: '1. Mahnung',
  4: '2. Mahnung',
  5: 'Inkasso',
  6: 'Anwalt',
  7: 'Klage',
  8: 'Vollstreckung',
  9: 'Bezahlt',
};

// ============================================================================
// Quick-Sort-Button Konfiguration (Issue #652)
// ============================================================================

const QUICK_SORT_BUTTONS: { key: QuickSortKey; icon: React.ElementType; label: string; title?: string }[] = [
  { key: 'hoechste_betraege', icon: DollarSign,   label: 'Höchste Beträge' },
  { key: 'laengste_offen',    icon: Timer,         label: 'Längsten offen' },
  { key: 'hoechste_mahnstufe',icon: Flame,         label: 'Höchste Mahnstufe' },
  { key: 'naechste_frist',    icon: CalendarClock, label: 'Nächste Frist', title: 'Fristdatum nicht server-seitig sortierbar — sortiert nach Eingang (älteste Akten zuerst)' },
];

// ============================================================================
// Hilfsfunktionen
// ============================================================================

function getDaysOpen(createdAt: string): number {
  const created = new Date(createdAt);
  const now = new Date();
  return Math.floor((now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24));
}

function getDringlichkeit(akte: Akte): 'normal' | 'warnung' | 'kritisch' {
  if (akte.status === 'abgeschlossen' || akte.status === 'storniert') return 'normal';
  const days = getDaysOpen(akte.created_at);
  if (days > 14) return 'kritisch';
  if (days > 7) return 'warnung';
  return 'normal';
}

function formatCurrency(val: number | null | undefined): string {
  if (val == null) return '—';
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(val);
}

// ============================================================================
// Mini-Progressbar (6 Dots für Tabellenzeile) — Issue #416
// ============================================================================

function MiniProgressDots({ hauptphase, zahlungsstatus, klage_status, stellungnahme_status, schriftsatzfrist }: {
  hauptphase?: number | null;
  zahlungsstatus?: string | null;
  klage_status?: string | null;
  stellungnahme_status?: string | null;
  schriftsatzfrist?: string | null;
}) {
  const phase = Math.min(Math.max(Number(hauptphase ?? 1), 1), 6);

  // Priority badge logic per issue spec
  let priorityLabel: string | null = null;
  let priorityClasses = '';

  // rot: Stellungnahme fällig (offen) oder Frist < 14d
  const stnFaellig = stellungnahme_status === 'offen';
  const fristKritisch = (() => {
    if (!schriftsatzfrist) return false;
    const days = Math.ceil((new Date(schriftsatzfrist).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    return days < 14;
  })();
  if (stnFaellig || fristKritisch) {
    priorityLabel = 'Stellungnahme fällig';
    priorityClasses = 'bg-red-50 text-red-700 border border-red-200';
  }
  // gelb: Teilweise bezahlt
  if (!priorityLabel && zahlungsstatus === 'teilweise_bezahlt') {
    priorityLabel = 'Teilweise bezahlt';
    priorityClasses = 'bg-amber-50 text-amber-700 border border-amber-200';
  }
  // rot: Klage anhängig
  if (!priorityLabel && klage_status && ['klage_erhoben', 'beweisaufnahme', 'urteil', 'berufung'].includes(klage_status)) {
    priorityLabel = 'Klage anhängig';
    priorityClasses = 'bg-red-50 text-red-700 border border-red-200';
  }

  return (
    <div data-testid="progress-column" className="flex flex-col gap-0.5 items-start">
      <div className="flex items-center gap-0.5">
        {HAUPTPHASEN_LABELS.map((label, idx) => {
          const p = idx + 1;
          const isDone   = p < phase;
          const isActive = p === phase;
          return (
            <div
              key={p}
              data-testid={`mini-dot-${p}`}
              data-aktiv={isActive ? 'true' : undefined}
              title={`Phase ${p}: ${label}`}
              className={[
                'rounded-full',
                isActive ? 'w-2.5 h-2.5' : 'w-2 h-2',
                isDone   ? 'bg-gray-400' : '',
                isActive ? 'bg-blue-600' : '',
                !isDone && !isActive ? 'bg-gray-200 border border-gray-300' : '',
              ].join(' ')}
            />
          );
        })}
      </div>
      {priorityLabel && (
        <span className={`text-[9px] font-semibold px-1 py-0.5 rounded ${priorityClasses}`}>
          {priorityLabel}
        </span>
      )}
    </div>
  );
}

// ============================================================================
// Sub-Status-Filter-Dropdown — Issue #416
// ============================================================================

// ─── SortHeaderIcon ───────────────────────────────────────────────────────────

function SortHeaderIcon({ column, sortState }: { column: string; sortState: AktenSortState }) {
  if (sortState.column !== column || !sortState.direction) {
    return <ArrowUpDown className="w-3 h-3 opacity-30 group-hover/th:opacity-60" />;
  }
  return sortState.direction === 'asc'
    ? <ArrowUp className="w-3 h-3 text-blue-600" />
    : <ArrowDown className="w-3 h-3 text-blue-600" />;
}

// ─── CountdownPillBadge ───────────────────────────────────────────────────────

function CountdownPillBadge({ faelligAm }: { faelligAm: string | null | undefined }) {
  const pill = berechneCountdownPill(faelligAm);
  if (!pill) return <span className="text-[10px] text-gray-300">—</span>;
  const cls =
    pill.level === 'gruen'      ? 'bg-green-50 text-green-700 border border-green-200' :
    pill.level === 'heute'      ? 'bg-amber-50 text-amber-700 border border-amber-200' :
                                  'bg-red-50 text-red-700 border border-red-200';
  const label =
    pill.level === 'ueberfaellig' ? `${Math.abs(pill.tage)}d überfällig` :
    pill.level === 'heute'        ? 'Heute' :
                                    `in ${pill.tage}d`;
  return (
    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full inline-flex items-center ${cls}`}>
      {label}
    </span>
  );
}

// ─── SubstatusFilterDropdown ──────────────────────────────────────────────────

interface SubstatusFilterDropdownProps {
  value: AktenSubstatusFilter;
  onChange: (next: AktenSubstatusFilter) => void;
}

function SubstatusFilterDropdown({ value, onChange }: SubstatusFilterDropdownProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  function toggle(section: keyof AktenSubstatusFilter, val: string) {
    const current: string[] = (value[section] as string[] | undefined) ?? [];
    const next = current.includes(val)
      ? current.filter(v => v !== val)
      : [...current, val];
    onChange({ ...value, [section]: next });
  }

  const activeCount =
    (value.zahlungsstatus?.length ?? 0) +
    (value.klage_status?.length ?? 0) +
    (value.warten_auf?.length ?? 0) +
    (value.stellungnahme?.length ?? 0);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        data-testid="substatus-filter"
        aria-expanded={open}
        onClick={() => setOpen(v => !v)}
        className={`flex items-center gap-1.5 border rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-gray-300 bg-white transition-colors ${
          activeCount > 0
            ? 'border-blue-400 text-blue-700'
            : 'border-gray-200 text-gray-600'
        }`}
      >
        <span>Sub-Status{activeCount > 0 ? ` (${activeCount})` : ''}</span>
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-1 z-50 bg-white border border-gray-200 rounded-xl shadow-lg p-3 min-w-[220px] space-y-3">
          {/* Zahlung */}
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-1.5">Zahlung</div>
            {ZAHLUNGSSTATUS_OPTIONS.map(opt => (
              <label key={opt.value} className="flex items-center gap-2 py-0.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={(value.zahlungsstatus ?? []).includes(opt.value)}
                  onChange={() => toggle('zahlungsstatus', opt.value)}
                  data-testid={`substatus-opt-${opt.value}`}
                  className="w-3 h-3 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-xs text-gray-700">{opt.label}</span>
              </label>
            ))}
          </div>

          {/* Stellungnahme */}
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-1.5">Stellungnahme</div>
            {STELLUNGNAHME_STATUS_FILTER_OPTIONS.filter(o => o.value !== 'alle').map(opt => (
              <label key={opt.value} className="flex items-center gap-2 py-0.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={(value.stellungnahme ?? []).includes(opt.value)}
                  onChange={() => toggle('stellungnahme', opt.value)}
                  data-testid={`substatus-opt-stn-${opt.value}`}
                  className="w-3 h-3 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-xs text-gray-700">{opt.label}</span>
              </label>
            ))}
          </div>

          {/* Klage */}
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-1.5">Klage</div>
            {KLAGE_STATUS_OPTIONS.map(opt => (
              <label key={opt.value} className="flex items-center gap-2 py-0.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={(value.klage_status ?? []).includes(opt.value)}
                  onChange={() => toggle('klage_status', opt.value)}
                  data-testid={`substatus-opt-${opt.value}`}
                  className="w-3 h-3 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-xs text-gray-700">{opt.label}</span>
              </label>
            ))}
          </div>

          {/* Warten auf */}
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-1.5">Warten auf</div>
            {WARTEN_AUF_OPTIONS.map(opt => (
              <label key={opt.value} className="flex items-center gap-2 py-0.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={(value.warten_auf ?? []).includes(opt.value)}
                  onChange={() => toggle('warten_auf', opt.value)}
                  data-testid={`substatus-opt-${opt.value}`}
                  className="w-3 h-3 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-xs text-gray-700">{opt.label}</span>
              </label>
            ))}
          </div>

          {activeCount > 0 && (
            <button
              type="button"
              onClick={() => onChange({})}
              className="w-full text-[10px] text-gray-400 hover:text-gray-600 flex items-center gap-0.5 pt-1 border-t border-gray-100"
            >
              <X className="w-3 h-3" /> Sub-Status-Filter zurücksetzen
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Erweiterter Filter-Bar (Issue #653) — 7 neue Filter-Kriterien
// ============================================================================

interface ErweiterterFilterBarProps {
  value: AktenErweiterterFilter;
  onChange: (next: AktenErweiterterFilter) => void;
}

function MultiSelectPills({
  options,
  selected,
  onToggle,
  testPrefix,
}: {
  options: readonly { value: string; label: string }[];
  selected: string[];
  onToggle: (val: string) => void;
  testPrefix: string;
}) {
  return (
    <div className="flex flex-wrap gap-1">
      {options.map(o => {
        const active = selected.includes(o.value);
        return (
          <button
            key={o.value}
            type="button"
            data-testid={`${testPrefix}-${o.value}`}
            onClick={() => onToggle(o.value)}
            className={`text-[10px] font-medium px-2 py-0.5 rounded-full border transition-colors ${
              active
                ? 'bg-blue-600 border-blue-600 text-white'
                : 'bg-white border-gray-200 text-gray-600 hover:border-blue-300 hover:text-blue-600'
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function StagePills({
  selected,
  onToggle,
}: {
  selected: number[];
  onToggle: (stage: number) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1">
      {Array.from({ length: 10 }, (_, i) => i).map(stage => {
        const active = selected.includes(stage);
        const classes = STAGE_PILL_CLASSES[stage] ?? 'bg-gray-100 text-gray-600';
        return (
          <button
            key={stage}
            type="button"
            data-testid={`stufe-pill-${stage}`}
            onClick={() => onToggle(stage)}
            className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border transition-colors ${
              active
                ? `${classes} border-current ring-1 ring-current`
                : 'bg-white border-gray-200 text-gray-500 hover:border-gray-400'
            }`}
          >
            {stage}: {STAGE_LABELS[stage]}
          </button>
        );
      })}
    </div>
  );
}

function BetragSlider({
  min,
  max,
  onChange,
}: {
  min: number;
  max: number;
  onChange: (min: number, max: number) => void;
}) {
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <input
          type="range"
          min={0}
          max={DEFAULT_BETRAG_MAX}
          step={100}
          value={min}
          data-testid="betrag-slider-min"
          onChange={e => onChange(Number(e.target.value), Math.max(Number(e.target.value), max))}
          className="flex-1 h-1 accent-blue-600"
        />
        <input
          type="range"
          min={0}
          max={DEFAULT_BETRAG_MAX}
          step={100}
          value={max}
          data-testid="betrag-slider-max"
          onChange={e => onChange(Math.min(min, Number(e.target.value)), Number(e.target.value))}
          className="flex-1 h-1 accent-blue-600"
        />
      </div>
      <div className="flex items-center justify-between text-[10px] text-gray-500">
        <span>{min.toLocaleString('de-DE')} €</span>
        <span>{max.toLocaleString('de-DE')} €</span>
      </div>
    </div>
  );
}

function ErweiterterFilterBar({ value, onChange }: ErweiterterFilterBarProps) {
  const [open, setOpen] = useState(false);
  const isActive = hatErweitertenFilter(value);

  function toggleMulti(field: keyof Pick<AktenErweiterterFilter, 'bezahl_status' | 'alter_bucket' | 'faellig_seit' | 'created_via'>, val: string) {
    const current = (value[field] as string[] | undefined) ?? [];
    const next = current.includes(val) ? current.filter(v => v !== val) : [...current, val];
    onChange({ ...value, [field]: next.length > 0 ? next : undefined });
  }

  function toggleMitFrist(val: string) {
    const current = value.mit_frist ?? [];
    let next: string[];
    if (current.includes(val)) {
      next = current.filter(v => v !== val);
    } else if (val === 'keine') {
      // 'keine' is mutually exclusive with 'aktiv'/'ueberfaellig'
      next = ['keine'];
    } else {
      // 'aktiv'/'ueberfaellig' deactivates 'keine'
      next = [...current.filter(v => v !== 'keine'), val];
    }
    onChange({ ...value, mit_frist: next.length > 0 ? next : undefined });
  }

  function toggleStage(stage: number) {
    const current = value.forderungs_stufe ?? [];
    const next = current.includes(stage) ? current.filter(s => s !== stage) : [...current, stage];
    onChange({ ...value, forderungs_stufe: next.length > 0 ? next : undefined });
  }

  return (
    <div className="w-full">
      <button
        type="button"
        data-testid="erweiterter-filter-toggle"
        aria-expanded={open}
        onClick={() => setOpen(v => !v)}
        className={`flex items-center gap-1.5 border rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-gray-300 bg-white transition-colors ${
          isActive
            ? 'border-blue-400 text-blue-700'
            : 'border-gray-200 text-gray-600 hover:border-gray-300'
        }`}
      >
        <span>Erw. Filter{isActive ? ' ●' : ''}</span>
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          data-testid="erweiterter-filter-panel"
          className="mt-2 bg-white border border-gray-200 rounded-xl shadow-lg p-4 space-y-4"
        >
          {/* Bezahl-Status */}
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-1.5">
              Bezahl-Status
            </div>
            <MultiSelectPills
              options={BEZAHL_STATUS_OPTIONS}
              selected={value.bezahl_status ?? []}
              onToggle={v => toggleMulti('bezahl_status', v)}
              testPrefix="bezahl"
            />
          </div>

          {/* Forderungs-Stufe */}
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-1.5">
              Forderungs-Stufe
            </div>
            <StagePills
              selected={value.forderungs_stufe ?? []}
              onToggle={toggleStage}
            />
          </div>

          {/* Forderung offen (Toggle) */}
          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                data-testid="forderung-offen-toggle"
                checked={value.forderung_offen ?? false}
                onChange={e => onChange({ ...value, forderung_offen: e.target.checked || undefined })}
                className="w-3 h-3 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-xs text-gray-700 font-medium">Nur Akten mit offener Forderung</span>
            </label>
          </div>

          {/* Alter-Bucket */}
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-1.5">
              Alter der Akte
            </div>
            <MultiSelectPills
              options={ALTER_BUCKET_OPTIONS}
              selected={value.alter_bucket ?? []}
              onToggle={v => toggleMulti('alter_bucket', v)}
              testPrefix="alter"
            />
          </div>

          {/* Fällig seit (aus akten_fristen) */}
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-1.5">
              Fällig seit (Frist)
            </div>
            <MultiSelectPills
              options={FAELLIG_SEIT_OPTIONS}
              selected={value.faellig_seit ?? []}
              onToggle={v => toggleMulti('faellig_seit', v)}
              testPrefix="faellig"
            />
          </div>

          {/* Mit Frist */}
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-1.5">
              Fristen-Status
            </div>
            <MultiSelectPills
              options={MIT_FRIST_OPTIONS}
              selected={value.mit_frist ?? []}
              onToggle={toggleMitFrist}
              testPrefix="frist"
            />
          </div>

          {/* Ersteller */}
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-1.5">
              Ersteller
            </div>
            <MultiSelectPills
              options={CREATED_VIA_OPTIONS}
              selected={value.created_via ?? []}
              onToggle={v => toggleMulti('created_via', v)}
              testPrefix="ersteller"
            />
          </div>

          {/* Betragsbereich */}
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-1.5">
              Betragsbereich (Rechnung)
            </div>
            <BetragSlider
              min={value.betrag_min ?? 0}
              max={value.betrag_max ?? DEFAULT_BETRAG_MAX}
              onChange={(min, max) =>
                onChange({
                  ...value,
                  betrag_min: min > 0 ? min : undefined,
                  betrag_max: max < DEFAULT_BETRAG_MAX ? max : undefined,
                })
              }
            />
          </div>

          {isActive && (
            <button
              type="button"
              data-testid="erweiterter-filter-reset"
              onClick={() => onChange({})}
              className="w-full text-[10px] text-gray-400 hover:text-gray-600 flex items-center gap-0.5 pt-1 border-t border-gray-100"
            >
              <X className="w-3 h-3" /> Erweiterte Filter zurücksetzen
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Statistik-Leiste
// ============================================================================

function StatistikLeiste({ stats: statValues, betriebColor }: { stats: { heuteNeu: number; dieseWoche: number; offenGesamt: number; abgeschlossenMonat: number }; betriebColor: string }) {
  const stats = [
    { label: 'Heute neu', value: statValues.heuteNeu, icon: Plus, color: betriebColor },
    { label: 'Diese Woche', value: statValues.dieseWoche, icon: Calendar, color: '#7C3AED' },
    { label: 'Offen gesamt', value: statValues.offenGesamt, icon: FolderOpen, color: '#D97706' },
    { label: 'Abgeschl. Monat', value: statValues.abgeschlossenMonat, icon: TrendingUp, color: '#059669' },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
      {stats.map(s => (
        <div key={s.label} className="bg-white border border-gray-100 rounded-lg px-4 py-3 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: s.color + '14' }}>
            <s.icon className="w-4 h-4" style={{ color: s.color }} />
          </div>
          <div>
            <div className="text-xl font-semibold text-gray-900 leading-none">{s.value}</div>
            <div className="text-[11px] text-gray-400 mt-0.5">{s.label}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ============================================================================
// Fahrzeug-Thumbnail
// ============================================================================

function AkteThumbnail({ url, size = 'sm' }: { url?: string; size?: 'sm' | 'md' }) {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const dim = size === 'md' ? 'w-14 h-14' : 'w-10 h-10';

  return (
    <div
      data-testid="akte-thumbnail"
      className={`${dim} rounded-lg overflow-hidden bg-gray-100 border border-gray-200 flex-shrink-0 relative group/thumb`}
    >
      {url && !error ? (
        <>
          <img
            src={url}
            alt="Fahrzeug"
            loading="lazy"
            onLoad={() => setLoaded(true)}
            onError={() => setError(true)}
            className={`w-full h-full object-cover transition-all duration-300 group-hover/thumb:scale-110 group-hover/thumb:brightness-105 ${loaded ? 'opacity-100' : 'opacity-0'}`}
          />
          {!loaded && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-3 h-3 border border-gray-300 border-t-gray-500 rounded-full animate-spin" />
            </div>
          )}
        </>
      ) : (
        <div className="w-full h-full flex items-center justify-center text-gray-300 group-hover/thumb:text-gray-400 transition-colors">
          <Camera className={size === 'md' ? 'w-5 h-5' : 'w-4 h-4'} />
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Stellungnahme-Chip + Schriftsatzfrist-Badge (Issue #413)
// ============================================================================

function StellungnahmeChip({
  status,
  schriftsatzfrist,
}: {
  status: string | null | undefined;
  schriftsatzfrist: string | null | undefined;
}) {
  const label = stellungnahmeStatusLabel(status);
  const chipClasses = stellungnahmeStatusChipClasses(status);
  const badge = label ? berechneSchriftsatzfristBadge(schriftsatzfrist) : null;

  if (!label || !chipClasses) return null;

  return (
    <div
      className="flex items-center gap-1 flex-wrap"
      data-testid="stellungnahme-chip"
      data-stellungnahme-status={status ?? ''}
    >
      <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${chipClasses}`}>
        Stellungnahme: {label}
      </span>
      {badge && badge.level !== 'normal' && (
        <span
          className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${schriftsatzfristBadgeClasses(badge.level)}`}
          data-testid="schriftsatzfrist-badge"
          data-schriftsatzfrist-level={badge.level}
        >
          {schriftsatzfristBadgeText(badge)}
        </span>
      )}
    </div>
  );
}

// ============================================================================
// Kanban-Karte
// ============================================================================

function KanbanKarte({ akte, onClick, betriebColor, thumbnailUrl, onOpenForderungsDrawer, onOpenFristModal }: { akte: Akte; onClick: () => void; betriebColor: string; thumbnailUrl?: string; onOpenForderungsDrawer: (e: React.MouseEvent, akteId: string) => void; onOpenFristModal?: (e: React.MouseEvent, akteId: string) => void }) {
  const dringlichkeit = getDringlichkeit(akte);
  const daysOpen = getDaysOpen(akte.created_at);
  const kundenName = akte.kunde_name || akte.titel;
  const fahrzeug = [akte.fahrzeug_marke, akte.fahrzeug_modell].filter(Boolean).join(' ');

  return (
    <button
      onClick={onClick}
      className="w-full text-left bg-white border border-gray-100 rounded-lg p-3 hover:shadow-md hover:border-gray-200 transition-all group relative"
      style={{
        borderLeftWidth: '3px',
        borderLeftColor: dringlichkeit === 'kritisch' ? '#DC2626' : dringlichkeit === 'warnung' ? '#F59E0B' : betriebColor,
      }}
    >
      {/* Header: Aktenzeichen + Tage + Thumbnail */}
      <div className="flex items-start gap-2.5 mb-1.5">
        <AkteThumbnail url={thumbnailUrl} size="md" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-mono font-medium text-gray-400">{akte.akten_nr}</span>
            {dringlichkeit !== 'normal' && (
              <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${
                dringlichkeit === 'kritisch' ? 'bg-red-50 text-red-600' : 'bg-amber-50 text-amber-600'
              }`}>
                {daysOpen}d
              </span>
            )}
          </div>
          {/* Kennzeichen + Fahrzeug (inline with thumbnail) */}
          {(akte.kennzeichen || akte.fahrzeug_kennzeichen) && (
            <div className="flex items-center gap-1 mt-0.5">
              <span className="text-xs font-medium text-gray-700 truncate">{akte.kennzeichen || akte.fahrzeug_kennzeichen}</span>
            </div>
          )}
          {fahrzeug && <span className="text-[10px] text-gray-400 truncate block">{fahrzeug}</span>}
        </div>
      </div>

      {/* Auftraggeber */}
      <div className="flex items-center gap-1.5 mb-2">
        <Users className="w-3 h-3 text-gray-300 flex-shrink-0" />
        <span className="text-xs text-gray-600 truncate">{kundenName}</span>
      </div>

      {/* Auftragstyp Badge */}
      {(akte.auftragstyp_sv || akte.auftragsart) && (
        <span className="text-[9px] font-medium px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">
          {AUFTRAGSTYP_LABELS[akte.auftragstyp_sv || akte.auftragsart || ''] || akte.auftragstyp_sv || akte.auftragsart}
        </span>
      )}

      {/* Stellungnahme-Chip (Issue #413) */}
      <div className="mt-1.5">
        <StellungnahmeChip
          status={akte.stellungnahme_status}
          schriftsatzfrist={akte.schriftsatzfrist}
        />
      </div>

      {/* Prüfungs-QS-Badge (Issue #463) */}
      {akte.pruefung_status && akte.pruefung_status !== 'keine' && (
        <div className="mt-1">
          <PruefungBadge status={akte.pruefung_status} />
        </div>
      )}

      {/* Beträge */}
      {akte.reparaturkosten_brutto != null && (
        <div className="mt-2 text-[10px] text-gray-400">
          Rep. {formatCurrency(akte.reparaturkosten_brutto)}
          {akte.wiederbeschaffungswert != null && <> | WBW {formatCurrency(akte.wiederbeschaffungswert)}</>}
        </div>
      )}

      {/* Instagram-Badge */}
      {(akte as any).instagram_einwilligung && (
        <div className="flex items-center gap-1 mt-1">
          <Instagram className="w-3 h-3 text-pink-500" />
          <span className="text-[9px] text-pink-500 font-medium">IG-Einwilligung</span>
        </div>
      )}
      {/* Quick-Action-Buttons (on hover) */}
      <div className="mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <QuickActionErrorBoundary resetKey={akte.id}>
          <QuickActionButtons
            akteId={akte.id}
            aktenNr={akte.akten_nummer || akte.akten_nr}
            versicherung_email={akte.versicherung_email}
            business_unit={akte.business_unit}
            forderungsakte_id={akte.forderungsakte_id}
            zahlungsstatus={akte.zahlungsstatus}
            onNavigateToAkte={(e) => { e.stopPropagation(); onClick(); }}
            onOpenForderungsDrawer={onOpenForderungsDrawer}
            onOpenFristModal={onOpenFristModal}
          />
        </QuickActionErrorBoundary>
      </div>

      {/* Datum + Ersteller */}
      <div className="flex items-center justify-between mt-2">
        <span className="text-[9px] text-gray-300">
          {new Date(akte.created_at).toLocaleDateString('de-DE')}
        </span>
        {akte.created_via && (
          <ErstellerBadge
            createdAt={akte.created_at}
            mitarbeiter={akte.creator_mitarbeiter ?? null}
            createdVia={akte.created_via}
            variant="mini"
          />
        )}
      </div>
    </button>
  );
}

// ============================================================================
// Neue Akte Modal
// ============================================================================

function NeueAkteModal({
  open, onClose, betriebId, betriebColor,
  prefillKunde = '', prefillSchadennummer = '', prefillSchadendatum = '',
}: {
  open: boolean;
  onClose: () => void;
  betriebId: BetriebId;
  betriebColor: string;
  prefillKunde?: string;
  prefillSchadennummer?: string;
  prefillSchadendatum?: string;
}) {
  const [kennzeichen, setKennzeichen] = useState('');
  const [auftraggeber, setAuftraggeber] = useState(prefillKunde);
  const [auftragstyp, setAuftragstyp] = useState('haftpflicht');
  const [saving, setSaving] = useState(false);
  // Erweiterte Felder
  const [showExtended, setShowExtended] = useState(
    !!(prefillSchadennummer || prefillSchadendatum),
  );
  const [schadennummer, setSchadennummer] = useState(prefillSchadennummer);
  const [versicherung, setVersicherung] = useState('');
  const [anwaltName, setAnwaltName] = useState('');
  const [schadendatum, setSchadendatum] = useState(prefillSchadendatum);
  const [istProblemfall, setIstProblemfall] = useState(false);
  const [problemBeschreibung, setProblemBeschreibung] = useState('');
  const [quelle, setQuelle] = useState('');
  // Mietwagen-spezifisch
  const [mietbeginn, setMietbeginn] = useState('');
  const [mietende, setMietende] = useState('');
  const [mietTagespreis, setMietTagespreis] = useState('65');

  const isMietwagen = betriebId === 'mietwagen';

  // Sync prefill fields whenever the modal opens (or prefill props change).
  // useState only uses the initial value on first mount, so without this
  // the form would always appear empty when opened after the first time.
  useEffect(() => {
    if (!open) return;
    setAuftraggeber(prefillKunde);
    setSchadennummer(prefillSchadennummer);
    setSchadendatum(prefillSchadendatum);
    setShowExtended(!!(prefillSchadennummer || prefillSchadendatum));
  }, [open, prefillKunde, prefillSchadennummer, prefillSchadendatum]);

  if (!open) return null;

  const handleCreate = async () => {
    if (!kennzeichen.trim() || !auftraggeber.trim()) {
      toast.error(isMietwagen ? 'Bitte Kennzeichen und Kunde ausfüllen' : 'Bitte Kennzeichen und Auftraggeber ausfüllen');
      return;
    }
    setSaving(true);

    // Retry loop for unique constraint violations (collision-resistant ID generation)
    const MAX_RETRIES = 3;
    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      try {
        const aktenNr = generiereAktenNr();
        const aktenNummer = generiereAktenNummer(betriebId, aktenNr);

        let insertData: Record<string, unknown>;

        if (isMietwagen) {
          insertData = erstelleMietwagenInsertData(
            {
              kundeName: auftraggeber,
              kennzeichen,
              versicherung,
              schadennummer,
              mietbeginn: mietbeginn || undefined,
              mietende: mietende || undefined,
              tagespreis: mietTagespreis ? parseFloat(mietTagespreis) : undefined,
              schadendatum: schadendatum || undefined,
              quelle: quelle || undefined,
            },
            aktenNr,
            aktenNummer,
          );
        } else {
          insertData = erstelleGutachtenInsertData({
            aktenNr,
            aktenNummer,
            betriebId,
            auftragstyp,
            auftragsTypLabels: AUFTRAGSTYP_LABELS,
            kennzeichen,
            auftraggeber,
            istProblemfall,
            schadennummer: schadennummer || undefined,
            versicherung: versicherung || undefined,
            schadendatum: schadendatum || undefined,
            quelle: quelle || undefined,
            anwaltName: anwaltName || undefined,
            problemBeschreibung: problemBeschreibung || undefined,
          });
        }

        const { data, error } = await supabase.from('akten').insert(insertData).select('id').single();

        if (error) {
          // Retry on unique constraint violation (akten_nr collision)
          if (isUniqueViolation(error) && attempt < MAX_RETRIES - 1) continue;
          throw error;
        }

        toast.success(`Akte ${aktenNummer} erstellt`);

        // Automatische Aufgaben bei Problemfall (nur für Nicht-Mietwagen)
        if (!isMietwagen && istProblemfall && data?.id) {
          try {
            const aufgaben = erstelleProblemfallAufgaben({
              akteId: data.id,
              aktenNr,
              betriebId,
              kennzeichen,
              anwaltName: anwaltName || undefined,
              versicherung: versicherung || undefined,
              schadennummer: schadennummer || undefined,
              problemBeschreibung: problemBeschreibung || undefined,
            });
            for (const aufgabe of aufgaben) {
              await supabase.from('aufgaben').insert(aufgabe);
            }
            toast.success('2 Aufgaben automatisch erstellt', { description: 'Anwalt anschreiben + Abrechnungsschreiben prüfen' });
          } catch (taskErr) {
            console.warn('Aufgaben konnten nicht erstellt werden:', taskErr);
          }
        }

        onClose();
        return;
      } catch (err: any) {
        if (attempt === MAX_RETRIES - 1) {
          const msg = isUniqueViolation(err)
            ? 'Akten-Nr. Kollision — bitte erneut versuchen.'
            : `Fehler beim Erstellen: ${err.message}`;
          toast.error(msg);
        }
      }
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/30 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md sm:mx-4 overflow-hidden max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">{isMietwagen ? 'Neuer Mietwagen-Vorgang' : 'Neue Akte anlegen'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          {isMietwagen ? (
            /* ══════ MIETWAGEN-SPEZIFISCHES FORMULAR ══════ */
            <>
              {/* Kunde / Geschädigter */}
              <div>
                <label className="block text-[11px] font-medium text-gray-500 mb-1.5">Kunde / Geschädigter *</label>
                <input
                  type="text"
                  value={auftraggeber}
                  onChange={e => setAuftraggeber(e.target.value)}
                  placeholder="Max Mustermann"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-gray-400"
                />
              </div>
              {/* Kennzeichen (geschädigtes Fahrzeug) */}
              <div>
                <label className="block text-[11px] font-medium text-gray-500 mb-1.5">Kennzeichen (geschädigtes Fzg.) *</label>
                <input
                  type="text"
                  value={kennzeichen}
                  onChange={e => setKennzeichen(e.target.value.toUpperCase())}
                  placeholder="MK-AB 1234"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-gray-400 uppercase font-mono"
                />
              </div>
              {/* Versicherung + Schadennummer */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-medium text-gray-500 mb-1.5">Versicherung (Gegner)</label>
                  <input
                    type="text"
                    value={versicherung}
                    onChange={e => setVersicherung(e.target.value)}
                    placeholder="z.B. HUK, Allianz..."
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-gray-500 mb-1.5">Schadennummer</label>
                  <input
                    type="text"
                    value={schadennummer}
                    onChange={e => setSchadennummer(e.target.value)}
                    placeholder="z.B. 2261-033.653/5-874"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-300 font-mono"
                  />
                </div>
              </div>
              {/* Mietbeginn / Mietende */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-medium text-gray-500 mb-1.5">Mietbeginn</label>
                  <input
                    type="date"
                    value={mietbeginn}
                    onChange={e => setMietbeginn(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-gray-500 mb-1.5">Mietende (geplant)</label>
                  <input
                    type="date"
                    value={mietende}
                    onChange={e => setMietende(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
                  />
                </div>
              </div>
              {/* Tagespreis + Schadendatum */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-medium text-gray-500 mb-1.5">Tagespreis (€)</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={mietTagespreis}
                    onChange={e => setMietTagespreis(e.target.value)}
                    placeholder="65"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-gray-500 mb-1.5">Schadendatum</label>
                  <input
                    type="date"
                    value={schadendatum}
                    onChange={e => setSchadendatum(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
                  />
                </div>
              </div>
            </>
          ) : (
            /* ══════ GUTACHTEN / STANDARD FORMULAR ══════ */
            <>
              {/* Auftragstyp */}
              <div>
                <label className="block text-[11px] font-medium text-gray-500 mb-1.5">Auftragstyp *</label>
                <select
                  value={auftragstyp}
                  onChange={e => setAuftragstyp(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-gray-400"
                >
                  {Object.entries(AUFTRAGSTYP_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>
              {/* Kennzeichen */}
              <div>
                <label className="block text-[11px] font-medium text-gray-500 mb-1.5">Kennzeichen *</label>
                <input
                  type="text"
                  value={kennzeichen}
                  onChange={e => setKennzeichen(e.target.value.toUpperCase())}
                  placeholder="MK-AB-1234"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-gray-400 uppercase font-mono"
                />
              </div>
              {/* Auftraggeber */}
              <div>
                <label className="block text-[11px] font-medium text-gray-500 mb-1.5">Auftraggeber *</label>
                <input
                  type="text"
                  value={auftraggeber}
                  onChange={e => setAuftraggeber(e.target.value)}
                  placeholder="Max Mustermann"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-gray-400"
                />
              </div>
              {/* Erweiterte Felder Toggle */}
              <button
                type="button"
                onClick={() => setShowExtended(!showExtended)}
                className="w-full text-left text-[11px] font-medium text-blue-600 hover:text-blue-800 flex items-center gap-1"
              >
                <ChevronRight className={`w-3 h-3 transition-transform ${showExtended ? 'rotate-90' : ''}`} />
                {showExtended ? 'Weniger Felder' : 'Erweiterte Felder (Versicherung, Anwalt, Problemfall...)'}
              </button>

              {showExtended && (
                <div className="space-y-3 pt-2 border-t border-gray-100">
                  <div>
                    <label className="block text-[11px] font-medium text-gray-500 mb-1.5">Schadennummer</label>
                    <input
                      type="text"
                      value={schadennummer}
                      onChange={e => setSchadennummer(e.target.value)}
                      placeholder="z.B. 2261-033.653/5-874"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-300 font-mono"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-medium text-gray-500 mb-1.5">Versicherung</label>
                    <input
                      type="text"
                      value={versicherung}
                      onChange={e => setVersicherung(e.target.value)}
                      placeholder="z.B. LVM, HUK, Allianz..."
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[11px] font-medium text-gray-500 mb-1.5">Schadendatum</label>
                      <input
                        type="date"
                        value={schadendatum}
                        onChange={e => setSchadendatum(e.target.value)}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-medium text-gray-500 mb-1.5">Quelle</label>
                      <select
                        value={quelle}
                        onChange={e => setQuelle(e.target.value)}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
                      >
                        <option value="">Nicht angegeben</option>
                        <option value="telefon">Telefon</option>
                        <option value="email">E-Mail</option>
                        <option value="instagram">Instagram</option>
                        <option value="whatsapp">WhatsApp</option>
                        <option value="werkstatt">Werkstatt</option>
                        <option value="anwalt">Anwalt</option>
                        <option value="winvalue">WinValue</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-[11px] font-medium text-gray-500 mb-1.5">Anwalt / Kanzlei</label>
                    <input
                      type="text"
                      value={anwaltName}
                      onChange={e => setAnwaltName(e.target.value)}
                      placeholder="z.B. Kanzlei Voigt, Dortmund"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
                    />
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={istProblemfall}
                        onChange={e => setIstProblemfall(e.target.checked)}
                        className="w-4 h-4 rounded border-amber-300 text-amber-600 focus:ring-amber-500"
                      />
                      <span className="text-[12px] font-semibold text-amber-800">Problemfall / Kuerzung</span>
                    </label>
                    {istProblemfall && (
                      <textarea
                        value={problemBeschreibung}
                        onChange={e => setProblemBeschreibung(e.target.value)}
                        placeholder="Was wurde gekuerzt? (z.B. Wertminderung, SV-Kosten, Nebenkosten...)"
                        rows={3}
                        className="mt-2 w-full border border-amber-200 rounded-lg px-3 py-2 text-[12px] focus:outline-none focus:ring-2 focus:ring-amber-300 bg-white"
                      />
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-2 mobile-dialog-actions sm:pb-0">
          <button onClick={onClose} className="px-4 py-2 text-xs text-gray-500 hover:text-gray-700">Abbrechen</button>
          <button
            onClick={handleCreate}
            disabled={saving}
            className="px-4 py-2 text-xs font-medium text-white rounded-lg disabled:opacity-50 transition-colors"
            style={{ backgroundColor: betriebColor }}
          >
            {saving ? 'Wird erstellt...' : 'Akte erstellen'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Hauptkomponente
// ============================================================================

export default function AktenPage() {
  const params = useParams<{ betrieb: string }>();
  const betriebId = (params.betrieb || 'gutachten') as BetriebId;
  const betrieb = BETRIEBE[betriebId];
  const betriebColor = betrieb?.color?.accent || '#9B1C1C';
  const { openAkte, setAktenBetriebFilter } = useApp();
  const [location, setLocation] = useLocation();

  // Parse initial filter state from URL (Issue #416)
  // Wouter v3 useLocation() returns pathname only — use window.location.search for query params
  const initialUrlParams = useMemo(() => {
    return new URLSearchParams(window.location.search);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('alle');
  const [auftragsFilter, setAuftragsFilter] = useState('alle');
  const [stellungnahmeFilter, setStellungnahmeFilter] = useState('alle');
  const [viewMode, setViewMode] = useState<ViewMode>('tabelle');
  const [substatusFilter, setSubstatusFilter] = useState<AktenSubstatusFilter>(() => {
    const parse = (key: string): string[] | undefined => {
      const val = initialUrlParams.get(key);
      return val ? val.split(',').filter(Boolean) : undefined;
    };
    const z = parse('sub_z');
    const k = parse('sub_k');
    const w = parse('sub_w');
    const s = parse('sub_s');
    return {
      ...(z && { zahlungsstatus: z }),
      ...(k && { klage_status: k }),
      ...(w && { warten_auf: w }),
      ...(s && { stellungnahme: s }),
    };
  });
  const [kritischFilter, setKritischFilter] = useState<boolean>(
    initialUrlParams.get('kritisch') === '1'
  );

  // Erweiterter Filter (Issue #653): URL-Params haben Vorrang vor localStorage
  const [erweiterterFilter, setErweiterterFilter] = useState<AktenErweiterterFilter>(() => {
    const fromUrl = erweiterterFilterFromUrlParams(initialUrlParams);
    if (hatErweitertenFilter(fromUrl)) return fromUrl;
    const lsKey = `akten-filter-erw-${betriebId}`;
    return erweiterterFilterFromJson(localStorage.getItem(lsKey));
  });
  // Skip first mount so useState initializer value (URL-params or localStorage) is not clobbered.
  const erweiterterFilterFirstMountRef = useRef(true);

  const [showNeueAkte, setShowNeueAkte] = useState(false);
  const [prefillKunde, setPrefillKunde] = useState('');
  const [prefillSchadennummer, setPrefillSchadennummer] = useState('');
  const [prefillSchadendatum, setPrefillSchadendatum] = useState('');

  // Sort state (Issue #652): starts null so manualSort is null and
  // bestimmeAktenSortierung (Stellungnahme-Auto-Sort, Issue #243) is not blocked.
  // Per-Betrieb defaults are applied via getDefaultSort passed as defaultSort
  // to useAktenPaginated, which uses them as the non-Stellungnahme fallback.
  const [sortState, setSortState] = useState<AktenSortState>({ column: null, direction: null });
  const [activeQuickSort, setActiveQuickSort] = useState<QuickSortKey | null>(null);

  // Reset explicit sort when user navigates between Betriebe (component may stay mounted).
  useEffect(() => {
    setSortState({ column: null, direction: null });
    setActiveQuickSort(null);
  }, [betriebId]);

  // Reset erweiterterFilter when user navigates between Betriebe (Issue #927):
  // useState lazy initializer only runs once at mount, so we need this effect
  // to reload the correct per-Betrieb filter from localStorage on navigation.
  // Skip first mount so the useState initializer value is not overwritten — it
  // already applied URL-params-first priority correctly on the initial render.
  useEffect(() => {
    if (erweiterterFilterFirstMountRef.current) {
      erweiterterFilterFirstMountRef.current = false;
      return;
    }
    const lsKey = `akten-filter-erw-${betriebId}`;
    setErweiterterFilter(erweiterterFilterFromJson(localStorage.getItem(lsKey)));
  }, [betriebId]);

  const handleColumnSort = useCallback((column: string) => {
    setActiveQuickSort(null);
    setSortState(prev => toggleColumnSort(prev, column));
  }, []);

  const handleQuickSort = useCallback((key: QuickSortKey) => {
    setActiveQuickSort(prev => (prev === key ? null : key));
    setSortState({ column: null, direction: null });
  }, []);

  const manualSort = useMemo(() => {
    if (activeQuickSort) return applyQuickSort(activeQuickSort);
    return sortStateToAktenSortierung(sortState);
  }, [activeQuickSort, sortState]);

  const [forderungsDrawerOpen, setForderungsDrawerOpen] = useState(false);
  const [forderungsDrawerAkteId, setForderungsDrawerAkteId] = useState<string | undefined>(undefined);

  const handleOpenForderungsDrawer = useCallback((e: React.MouseEvent, akteId: string) => {
    e.stopPropagation();
    setForderungsDrawerAkteId(akteId);
    setForderungsDrawerOpen(true);
  }, []);

  const [fristModalOpen, setFristModalOpen] = useState(false);
  const [fristModalAkteId, setFristModalAkteId] = useState<string>('');
  const [fristModalAktenNummer, setFristModalAktenNummer] = useState<string | null>(null);
  // Open the NeueAkteModal with prefill when navigated here from the
  // Gutachten cross-link "Mietwagen-Akte anlegen" button (Issue #272).
  // Dep on `location` so this fires again when wouter navigates to the same
  // route pattern (/akten/:betrieb) while the component stays mounted.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('neue-akte') !== '1') return;
    setPrefillKunde(params.get('kunde') ?? '');
    setPrefillSchadennummer(params.get('schadennummer') ?? '');
    setPrefillSchadendatum(params.get('schadendatum') ?? '');
    setShowNeueAkte(true);
    // Clean up URL so back-navigation doesn't re-open the modal (Issue #873: preserve other params)
    window.history.replaceState(null, '', bereinigNeueAkteParam(window.location.pathname, window.location.search));
  }, [location]);

  // Tabellen-Ansicht nutzt server-seitige Pagination (50 pro Seite), damit
  // auch 6.600+ Akten flüssig laden. Kanban lädt momentan die gleiche Liste —
  // bei Bedarf später pro Spalte einzeln paginieren.
  const PAGE_SIZE = 50;
  // Stable reference: getDefaultSort returns a new object each call; without
  // useMemo the object identity changes every render, destabilising the
  // sortierung useMemo inside useAktenPaginated and causing an infinite
  // loadFirstPage loop via the useEffect([..., sortierung]) dependency.
  const defaultSort = useMemo(() => getDefaultSort(betriebId), [betriebId]);
  const {
    rows: filteredAkten,
    total,
    loading,
    loadingMore,
    error,
    hasMore,
    loadMore,
  } = useAktenPaginated({
    businessUnit: betriebId as BusinessUnit,
    search: searchQuery,
    status: statusFilter,
    auftragsart: auftragsFilter,
    stellungnahmeStatus: stellungnahmeFilter,
    substatusFilter,
    kritischFilter,
    erweiterterFilter,
    manualSort,
    defaultSort,
    pageSize: PAGE_SIZE,
  });
  const { data: statsData } = useAktenStats(betriebId as BusinessUnit);
  const aktenStats = statsData ?? { heuteNeu: 0, dieseWoche: 0, offenGesamt: 0, abgeschlossenMonat: 0 };

  // Fahrzeug-Thumbnails + Fristen für die aktuell sichtbaren Akten laden
  const akteIds = useMemo(() => filteredAkten.map(a => a.id), [filteredAkten]);
  const thumbnailUrls = useAktenThumbnails(akteIds);
  const naechsteFristenMap = useAktenFristenMin(akteIds);

  const handleOpenFristModal = useCallback((e: React.MouseEvent, akteId: string) => {
    e.stopPropagation();
    setFristModalAkteId(akteId);
    const akte = filteredAkten.find(a => a.id === akteId);
    setFristModalAktenNummer(akte ? aktenIdentifier(akte) : null);
    setFristModalOpen(true);
  }, [filteredAkten]);

  useEffect(() => {
    setAktenBetriebFilter(betriebId as BusinessUnit);
  }, [betriebId, setAktenBetriebFilter]);

  // Sync kritisch filter to URL query string (Issue #416)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const currentKritisch = params.get('kritisch') === '1';
    if (currentKritisch === kritischFilter) return;
    if (kritischFilter) {
      params.set('kritisch', '1');
    } else {
      params.delete('kritisch');
    }
    const newSearch = params.toString();
    const basePath = window.location.pathname;
    window.history.replaceState(null, '', newSearch ? `${basePath}?${newSearch}` : basePath);
  }, [kritischFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync sub-status filter to URL query string (Issue #416)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const setOrDelete = (key: string, vals: string[] | undefined) => {
      if (vals && vals.length > 0) params.set(key, vals.join(','));
      else params.delete(key);
    };
    setOrDelete('sub_z', substatusFilter.zahlungsstatus);
    setOrDelete('sub_k', substatusFilter.klage_status);
    setOrDelete('sub_w', substatusFilter.warten_auf);
    setOrDelete('sub_s', substatusFilter.stellungnahme);
    const newSearch = params.toString();
    const basePath = window.location.pathname;
    window.history.replaceState(null, '', newSearch ? `${basePath}?${newSearch}` : basePath);
  }, [substatusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync erweiterter Filter zu URL + localStorage (Issue #653)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const newErwParams = erweiterterFilterToUrlParams(erweiterterFilter);

    // Alte erw. Filter-Keys aus URL entfernen, neue setzen
    for (const key of ['bezahl', 'stufe', 'foffen', 'alter', 'faellig', 'frist', 'betrag_min', 'betrag_max']) {
      params.delete(key);
    }
    newErwParams.forEach((val, key) => params.set(key, val));

    const newSearch = params.toString();
    const basePath = window.location.pathname;
    window.history.replaceState(null, '', newSearch ? `${basePath}?${newSearch}` : basePath);

    const lsKey = `akten-filter-erw-${betriebId}`;
    if (hatErweitertenFilter(erweiterterFilter)) {
      localStorage.setItem(lsKey, erweiterterFilterToJson(erweiterterFilter));
    } else {
      localStorage.removeItem(lsKey);
    }
  }, [erweiterterFilter, betriebId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Infinite-Scroll-Trigger: letzter sichtbarer DOM-Node triggert loadMore
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore) return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) loadMore();
    }, { rootMargin: '200px' });
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, loadMore]);

  const handleOpenAkte = useCallback((akte: Akte) => {
    const kundenName = (akte as any).kunden
      ? `${(akte as any).kunden.nachname}, ${(akte as any).kunden.vorname}`
      : akte.kunde_name || akte.titel;
    // Prefer `akten_nummer` (NOT NULL) over the legacy nullable `akten_nr`
    // so `null` never lands in `activeAkte.aktenNr` or the URL — see #155.
    const identifier = aktenIdentifier(akte);
    if (!identifier) {
      toast.error('Akte hat keine gültige Akten-Nummer');
      return;
    }
    openAkte({
      id: akte.id,
      aktenNr: identifier,
      titel: akte.titel,
      kundenName,
      businessUnit: akte.business_unit as BusinessUnit,
    });
    setLocation(`/akten/${betriebId}/${identifier}/uebersicht`);
  }, [openAkte, setLocation, betriebId]);

  const substatusActiveCount =
    (substatusFilter.zahlungsstatus?.length ?? 0) +
    (substatusFilter.klage_status?.length ?? 0) +
    (substatusFilter.warten_auf?.length ?? 0) +
    (substatusFilter.stellungnahme?.length ?? 0);

  const activeFilters =
    (statusFilter !== 'alle' ? 1 : 0) +
    (auftragsFilter !== 'alle' ? 1 : 0) +
    (istStellungnahmeStatusFilterAktiv(stellungnahmeFilter) ? 1 : 0) +
    (substatusActiveCount > 0 ? 1 : 0) +
    (kritischFilter ? 1 : 0) +
    (hatErweitertenFilter(erweiterterFilter) ? 1 : 0);

  return (
    <div className="p-4 lg:p-6 space-y-4 max-w-[1440px] mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <div className="koop-icon-container flex-shrink-0">
            <FolderOpen className="w-5 h-5 koop-icon" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="text-base sm:text-lg font-bold text-foreground truncate">Akten — {betrieb?.name || betriebId}</h1>
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
                {betrieb?.kuerzel}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              {loading ? 'lade...' : (
                <>
                  {total.toLocaleString('de-DE')} {total === 1 ? 'Akte' : 'Akten'}
                  {(activeFilters > 0 || searchQuery) && ' (gefiltert)'}
                  {filteredAkten.length < total && ` · ${filteredAkten.length} geladen`}
                </>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* View Toggle */}
          <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
            <button
              onClick={() => setViewMode('tabelle')}
              className={`p-1.5 rounded-md transition-colors ${viewMode === 'tabelle' ? 'bg-white shadow-sm text-gray-700' : 'text-gray-400 hover:text-gray-600'}`}
              title="Tabellenansicht"
            >
              <List className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('kanban')}
              className={`p-1.5 rounded-md transition-colors ${viewMode === 'kanban' ? 'bg-white shadow-sm text-gray-700' : 'text-gray-400 hover:text-gray-600'}`}
              title="Kanban-Board"
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
          </div>
          {/* CSV-Export */}
          <button
            onClick={() => downloadAktenCsv(filteredAkten as AktenExportRow[])}
            title="Gefilterte Akten als CSV exportieren"
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-600 bg-white hover:bg-gray-50 transition-colors"
          >
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">Exportieren</span>
          </button>
          {/* Neue Akte */}
          <button
            onClick={() => setShowNeueAkte(true)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold text-white transition-colors"
            style={{ backgroundColor: betriebColor }}
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Neue Akte</span>
          </button>
        </div>
      </div>

      {/* Statistik */}
      <StatistikLeiste stats={aktenStats} betriebColor={betriebColor} />

      {/* Suche + Filter */}
      <div className="flex flex-col md:flex-row items-stretch md:items-center gap-2">
        {/* Suchfeld */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Kennzeichen, Aktenzeichen, Name, FIN suchen..."
            className="w-full pl-9 pr-8 py-2 border border-gray-200 rounded-lg text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-gray-400 bg-white"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* Filter Dropdowns */}
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-xs text-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-300 bg-white"
          >
            {STATUS_FILTER_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select
            value={auftragsFilter}
            onChange={e => setAuftragsFilter(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-xs text-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-300 bg-white"
          >
            {AUFTRAGSTYP_FILTER_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select
            aria-label="Stellungnahme-Filter"
            data-testid="stellungnahme-filter"
            value={stellungnahmeFilter}
            onChange={e => setStellungnahmeFilter(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-xs text-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-300 bg-white"
          >
            {STELLUNGNAHME_STATUS_FILTER_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>
                {o.value === 'alle' ? 'Stellungnahme: Alle' : `Stellungnahme: ${o.label}`}
              </option>
            ))}
          </select>

          {/* Sub-Status-Filter (Issue #416) */}
          <SubstatusFilterDropdown value={substatusFilter} onChange={setSubstatusFilter} />

          {/* Kritisch-Quickfilter (Issue #416) */}
          <button
            type="button"
            data-testid="kritisch-filter-btn"
            aria-pressed={kritischFilter}
            onClick={() => setKritischFilter(v => !v)}
            className={`flex items-center gap-1.5 border rounded-lg px-3 py-2 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-red-300 ${
              kritischFilter
                ? 'bg-red-600 border-red-600 text-white'
                : 'border-gray-200 text-gray-600 bg-white hover:border-red-300 hover:text-red-600'
            }`}
          >
            <ShieldAlert className="w-3.5 h-3.5" />
            Kritisch
          </button>

          {/* Erweiterter Filter (Issue #653) */}
          <ErweiterterFilterBar
            value={erweiterterFilter}
            onChange={setErweiterterFilter}
          />

          {activeFilters > 0 && (
            <button
              data-testid="alle-filter-reset"
              onClick={() => {
                setStatusFilter('alle');
                setAuftragsFilter('alle');
                setStellungnahmeFilter('alle');
                setSubstatusFilter({});
                setKritischFilter(false);
                setErweiterterFilter({});
              }}
              className="text-[10px] text-gray-400 hover:text-gray-600 flex items-center gap-0.5"
            >
              <X className="w-3 h-3" /> Reset
            </button>
          )}
        </div>
      </div>

      {/* Inhalt */}
      {loading ? (
        <div className="py-16 text-center">
          <div className="w-6 h-6 border-2 border-gray-200 border-t-gray-500 rounded-full animate-spin mx-auto mb-3" />
          <p className="text-xs text-gray-400">Akten werden geladen...</p>
        </div>
      ) : error ? (
        <div className="py-16 text-center text-sm text-red-600">Fehler: {error}</div>
      ) : filteredAkten.length === 0 ? (
        <div className="py-16 text-center">
          <FolderOpen className="w-10 h-10 text-gray-200 mx-auto mb-3" />
          <p className="text-sm text-gray-500 font-medium">Keine Akten gefunden</p>
          <p className="text-xs text-gray-400 mt-1">
            {searchQuery || activeFilters > 0
              ? 'Versuche andere Suchbegriffe oder Filter'
              : 'Erstelle eine neue Akte mit dem Button oben rechts'}
          </p>
          {(searchQuery || activeFilters > 0) && (
            <button
              onClick={() => {
                setSearchQuery('');
                setStatusFilter('alle');
                setAuftragsFilter('alle');
                setStellungnahmeFilter('alle');
                setSubstatusFilter({});
                setKritischFilter(false);
                setErweiterterFilter({});
              }}
              className="mt-3 text-xs font-medium hover:underline"
              style={{ color: betriebColor }}
            >
              Alle Filter zurücksetzen
            </button>
          )}
        </div>
      ) : viewMode === 'tabelle' ? (
        /* ============ TABELLEN-ANSICHT ============ */
        <div className="koop-card-static overflow-hidden">
          {/* Quick-Sort Buttons (Issue #652) */}
          <div className="hidden lg:flex items-center gap-2 px-4 py-2 border-b border-gray-100 bg-gray-50/60 flex-wrap">
            <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Schnellsortierung:</span>
            {QUICK_SORT_BUTTONS.map(({ key, icon: Icon, label, title }) => (
              <button
                key={key}
                type="button"
                title={title}
                onClick={() => handleQuickSort(key)}
                className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-full border transition-colors ${
                  activeQuickSort === key
                    ? 'text-white border-transparent'
                    : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300 hover:text-gray-700'
                }`}
                style={activeQuickSort === key ? { backgroundColor: betriebColor, borderColor: betriebColor } : undefined}
              >
                <Icon className="w-3 h-3" />
                {label}
              </button>
            ))}
            {(activeQuickSort || sortState.column) && (
              <button
                type="button"
                onClick={() => { setActiveQuickSort(null); setSortState({ column: null, direction: null }); }}
                className="text-[10px] text-gray-400 hover:text-gray-600 flex items-center gap-0.5 ml-1"
              >
                <X className="w-3 h-3" /> Sort reset
              </button>
            )}
          </div>
          {/* Table Header — scrollable for wide columns */}
          <div className="overflow-x-auto">
          <div className="hidden lg:grid gap-2 px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400 bg-gray-50 border-b border-gray-200 min-w-[1300px]"
            style={{ gridTemplateColumns: '40px 0.7fr 1fr 1.2fr 0.6fr 0.6fr 0.5fr 0.6fr 0.7fr 0.7fr 0.7fr 0.8fr 0.5fr 0.4fr' }}>
            <span>Foto</span>
            <button type="button" onClick={() => handleColumnSort('akten_nummer')} className="group/th flex items-center gap-1 text-left hover:text-gray-600">
              Akten-Nr <SortHeaderIcon column="akten_nummer" sortState={sortState} />
            </button>
            <button type="button" onClick={() => handleColumnSort('kennzeichen')} className="group/th flex items-center gap-1 text-left hover:text-gray-600">
              Kennzeichen <SortHeaderIcon column="kennzeichen" sortState={sortState} />
            </button>
            <button type="button" onClick={() => handleColumnSort('kunde_name')} className="group/th flex items-center gap-1 text-left hover:text-gray-600">
              Auftraggeber <SortHeaderIcon column="kunde_name" sortState={sortState} />
            </button>
            {betriebId === 'mietwagen' ? (
              <button type="button" onClick={() => handleColumnSort('versicherung')} className="group/th flex items-center gap-1 text-left hover:text-gray-600">
                Versicherung <SortHeaderIcon column="versicherung" sortState={sortState} />
              </button>
            ) : (
              <span>Typ</span>
            )}
            <button type="button" onClick={() => handleColumnSort('status')} className="group/th flex items-center gap-1 text-left hover:text-gray-600">
              Status <SortHeaderIcon column="status" sortState={sortState} />
            </button>
            <button type="button" onClick={() => handleColumnSort('gesamtbetrag')} className="group/th flex items-center gap-1 text-left hover:text-gray-600">
              Betrag <SortHeaderIcon column="gesamtbetrag" sortState={sortState} />
            </button>
            {/* Progress (Issue #416) */}
            <span>Progress</span>
            {/* 4 neue gemeinsame Spalten (Issue #652) */}
            <button type="button" onClick={() => handleColumnSort('rechnung_datum')} className="group/th flex items-center gap-1 text-left hover:text-gray-600">
              Rg. offen seit <SortHeaderIcon column="rechnung_datum" sortState={sortState} />
            </button>
            <button type="button" onClick={() => handleColumnSort('faellig_seit')} className="group/th flex items-center gap-1 text-left hover:text-gray-600">
              Fällig seit <SortHeaderIcon column="faellig_seit" sortState={sortState} />
            </button>
            <button type="button" onClick={() => handleColumnSort('forderung_stage')} className="group/th flex items-center gap-1 text-left hover:text-gray-600">
              Mahnstufe <SortHeaderIcon column="forderung_stage" sortState={sortState} />
            </button>
            <span>Nächste Frist</span>
            {/* Betriebs-spezifisch */}
            <span>{
              betriebId === 'gutachten'  ? 'Schadennummer' :
              betriebId === 'autoglas'   ? 'Glasart' :
              betriebId === 'mietwagen'  ? 'Tagespreis' :
              betriebId === 'werkstatt'  ? 'Auftragsart' : 'Info'
            }</span>
            <button type="button" onClick={() => handleColumnSort('created_at')} className="group/th flex items-center gap-1 text-right justify-end hover:text-gray-600">
              Alter <SortHeaderIcon column="created_at" sortState={sortState} />
            </button>
          </div>
          {filteredAkten.map((akte, index) => {
            const dringlichkeit = getDringlichkeit(akte);
            const daysOpen = getDaysOpen(akte.created_at);
            const fahrzeug = [akte.fahrzeug_marke, akte.fahrzeug_modell].filter(Boolean).join(' ');
            const kundenName = (akte as any).kunden
              ? `${(akte as any).kunden.nachname}, ${(akte as any).kunden.vorname}`
              : akte.kunde_name || akte.titel;
            const isEven = index % 2 === 0;

            // Berechnete Spalten (Issue #652)
            const rechnungOffenSeit = berechneRechnungOffenSeit(akte.rechnung_datum, akte.zahlungsstatus);
            const faelligSeit = berechneFaelligSeit(akte.rechnung_datum);
            const naechsteFrist = naechsteFristenMap.get(akte.id) ?? null;

            return (
              <button
                key={akte.id}
                data-testid="akte-row"
                onClick={() => handleOpenAkte(akte)}
                className={`w-full lg:grid lg:gap-2 flex flex-col gap-1 px-3 sm:px-4 py-2.5 sm:py-3 transition-colors text-left group border-b border-gray-100 last:border-0 koop-list-row min-w-[1300px] ${
                  isEven ? 'bg-white' : 'bg-[#F9FAFB]'
                }`}
                style={{
                  gridTemplateColumns: '40px 0.7fr 1fr 1.2fr 0.6fr 0.6fr 0.5fr 0.6fr 0.7fr 0.7fr 0.7fr 0.8fr 0.5fr 0.4fr',
                  borderLeft: `3px solid ${
                    dringlichkeit === 'kritisch' ? '#DC2626' :
                    dringlichkeit === 'warnung' ? '#F59E0B' : betriebColor
                  }`,
                }}
              >
                {/* Fahrzeug-Thumbnail (Desktop) */}
                <div className="hidden lg:flex items-center">
                  <AkteThumbnail url={thumbnailUrls[akte.id]} />
                </div>

                {/* Akten-Nr + Thumbnail (Mobile) */}
                <div className="flex items-center gap-2 lg:hidden">
                  <AkteThumbnail url={thumbnailUrls[akte.id]} />
                  <div className="flex flex-col min-w-0">
                    <span className="text-[11px] font-mono font-medium text-gray-500">{akte.akten_nr}</span>
                    <span className="text-xs font-semibold text-gray-800 truncate">{akte.kennzeichen || akte.fahrzeug_kennzeichen || '—'}</span>
                    {fahrzeug && <span className="text-[10px] text-gray-400 truncate">{fahrzeug}</span>}
                  </div>
                </div>

                {/* Akten-Nr (Desktop) */}
                <span className="text-[11px] font-mono font-medium text-gray-500 hidden lg:block truncate">{akte.akten_nr}</span>

                {/* Kennzeichen + Fahrzeug (Desktop) */}
                <div className="flex-col min-w-0 hidden lg:flex">
                  <span className="text-xs font-semibold text-gray-800 truncate">{akte.kennzeichen || akte.fahrzeug_kennzeichen || '—'}</span>
                  {fahrzeug && <span className="text-[10px] text-gray-400 truncate">{fahrzeug}</span>}
                </div>

                {/* Auftraggeber */}
                <div className="flex flex-col min-w-0">
                  <span className="text-xs font-medium text-gray-700 truncate">{kundenName}</span>
                  {akte.erteilt_durch && akte.erteilt_durch !== kundenName && (
                    <span className="text-[10px] text-gray-400 truncate">via {akte.erteilt_durch}</span>
                  )}
                </div>

                {/* Auftragstyp / Versicherung (Mietwagen) */}
                {betriebId === 'mietwagen' ? (
                  <div className="flex flex-col min-w-0">
                    <span className="text-xs font-medium text-gray-700 truncate">{akte.versicherung || '—'}</span>
                  </div>
                ) : (
                  <div>
                    <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 truncate block">
                      {AUFTRAGSTYP_LABELS[akte.auftragstyp_sv || akte.auftragsart || ''] || akte.auftragsart || '—'}
                    </span>
                  </div>
                )}

                {/* Status */}
                <div className="flex flex-col gap-1 items-start">
                  <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${STATUS_COLORS[akte.status as AktenStatus] || 'bg-gray-100 text-gray-500'}`}>
                    {STATUS_LABELS[akte.status as AktenStatus] || akte.status}
                  </span>
                  <StellungnahmeChip
                    status={akte.stellungnahme_status}
                    schriftsatzfrist={akte.schriftsatzfrist}
                  />
                  {akte.pruefung_status && akte.pruefung_status !== 'keine' && (
                    <PruefungBadge status={akte.pruefung_status} />
                  )}
                </div>

                {/* Beträge */}
                <div className="flex flex-col min-w-0">
                  {akte.reparaturkosten_brutto != null ? (
                    <span className="text-[11px] font-medium text-gray-700">{formatCurrency(akte.reparaturkosten_brutto)}</span>
                  ) : akte.gesamtbetrag != null ? (
                    <span className="text-[11px] font-medium text-gray-700">{formatCurrency(akte.gesamtbetrag)}</span>
                  ) : (
                    <span className="text-[10px] text-gray-300">—</span>
                  )}
                </div>

                {/* Progress (Issue #416) */}
                <div className="hidden lg:flex items-center">
                  <MiniProgressDots
                    hauptphase={akte.hauptphase}
                    zahlungsstatus={akte.zahlungsstatus}
                    klage_status={akte.klage_status}
                    stellungnahme_status={akte.stellungnahme_status}
                    schriftsatzfrist={akte.schriftsatzfrist}
                  />
                </div>

                {/* Rechnung offen seit (Issue #652) */}
                <div className="hidden lg:flex items-center">
                  {rechnungOffenSeit != null ? (
                    <span className={`text-[11px] font-medium ${rechnungOffenSeit > 30 ? 'text-red-600' : rechnungOffenSeit > 14 ? 'text-amber-600' : 'text-gray-600'}`}>
                      {rechnungOffenSeit}d
                    </span>
                  ) : (
                    <span className="text-[10px] text-gray-300">—</span>
                  )}
                </div>

                {/* Fällig seit (Issue #652) */}
                <div className="hidden lg:flex items-center">
                  {faelligSeit != null ? (
                    <span className={`text-[11px] font-medium ${faelligSeit > 0 ? 'text-red-600' : faelligSeit === 0 ? 'text-amber-600' : 'text-gray-400'}`}>
                      {faelligSeit > 0 ? `+${faelligSeit}d` : `${faelligSeit}d`}
                    </span>
                  ) : (
                    <span className="text-[10px] text-gray-300">—</span>
                  )}
                </div>

                {/* Mahnstufe (Issue #652) */}
                <div className="hidden lg:flex items-center">
                  {akte.forderung_stage != null ? (
                    <ForderungStagePill
                      stage={akte.forderung_stage}
                      forderungsAkteId={akte.forderungsakte_id}
                    />
                  ) : (
                    <span className="text-[10px] text-gray-300">—</span>
                  )}
                </div>

                {/* Nächste Frist (Issue #652) */}
                <div className="hidden lg:flex items-center">
                  <CountdownPillBadge faelligAm={naechsteFrist} />
                </div>

                {/* Betriebs-spezifische Zusatzspalte (Issue #652) */}
                <div className="hidden lg:flex items-center">
                  {betriebId === 'gutachten' && (
                    <span className="text-[10px] text-gray-500 font-mono truncate">{akte.schadennummer || '—'}</span>
                  )}
                  {betriebId === 'autoglas' && (
                    <span className="text-[10px] text-gray-500 truncate">{akte.glasart || '—'}</span>
                  )}
                  {betriebId === 'mietwagen' && (
                    <span className="text-[10px] text-gray-500 truncate">
                      {akte.tagespreis != null ? `${akte.tagespreis} €/d` : '—'}
                    </span>
                  )}
                  {betriebId === 'werkstatt' && (
                    <span className="text-[10px] text-gray-500 truncate">{akte.auftragsart || '—'}</span>
                  )}
                </div>

                {/* Alter / Dringlichkeit */}
                <div className="flex flex-col items-end gap-0.5">
                  <div className="flex items-center justify-end gap-1">
                    {(akte as any).linked_akte_id && (
                      <span
                        title={betriebId === 'gutachten' ? 'Mit Mietwagen-Akte verknüpft' : 'Mit Kfz-Gutachten-Akte verknüpft'}
                        className={`inline-flex items-center justify-center w-4 h-4 rounded ${betriebId === 'gutachten' ? 'text-amber-500' : 'text-blue-600'}`}
                      >
                        {betriebId === 'gutachten' ? <Key className="w-3 h-3" /> : <Car className="w-3 h-3" />}
                      </span>
                    )}
                    {(akte as any).instagram_einwilligung && <Instagram className="w-3 h-3 text-pink-500" />}
                    {dringlichkeit === 'kritisch' && <AlertTriangle className="w-3 h-3 text-red-500" />}
                    {dringlichkeit === 'warnung' && <AlertTriangle className="w-3 h-3 text-amber-500" />}
                    <span className={`text-[11px] font-medium ${
                      dringlichkeit === 'kritisch' ? 'text-red-600' :
                      dringlichkeit === 'warnung' ? 'text-amber-600' : 'text-gray-400'
                    }`}>
                      {daysOpen}d
                    </span>
                  </div>
                  {akte.created_via && (
                    <ErstellerBadge
                      createdAt={akte.created_at}
                      mitarbeiter={akte.creator_mitarbeiter ?? null}
                      createdVia={akte.created_via}
                      variant="mini"
                    />
                  )}
                </div>

                {/* Quick-Action-Buttons (on hover, spans full row width) */}
                <div className="hidden lg:block col-span-full opacity-0 group-hover:opacity-100 transition-opacity pt-1">
                  <QuickActionErrorBoundary resetKey={akte.id}>
                    <QuickActionButtons
                      akteId={akte.id}
                      aktenNr={akte.akten_nummer || akte.akten_nr}
                      versicherung_email={akte.versicherung_email}
                      business_unit={akte.business_unit}
                      forderungsakte_id={akte.forderungsakte_id}
                      zahlungsstatus={akte.zahlungsstatus}
                      onNavigateToAkte={(e) => { e.stopPropagation(); handleOpenAkte(akte); }}
                      onOpenForderungsDrawer={handleOpenForderungsDrawer}
                      onOpenFristModal={handleOpenFristModal}
                    />
                  </QuickActionErrorBoundary>
                </div>
              </button>
            );
          })}
          </div>{/* end overflow-x-auto */}
          {/* Infinite-Scroll sentinel — triggert loadMore wenn sichtbar */}
          {hasMore && (
            <div
              ref={sentinelRef}
              className="px-4 py-3 flex items-center justify-center gap-2 text-[11px] text-gray-400 bg-gray-50/80 border-t border-gray-100"
            >
              {loadingMore ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  <span>Lade weitere Akten...</span>
                </>
              ) : (
                <button
                  onClick={loadMore}
                  className="text-xs font-medium text-gray-500 hover:text-gray-800"
                >
                  Mehr laden ({(total - filteredAkten.length).toLocaleString('de-DE')} verbleibend)
                </button>
              )}
            </div>
          )}
          {/* Footer */}
          <div className="px-4 py-2.5 bg-gray-50/80 border-t border-gray-100 flex items-center justify-between">
            <span className="text-[11px] text-gray-400">
              {filteredAkten.length.toLocaleString('de-DE')} / {total.toLocaleString('de-DE')} {total === 1 ? 'Akte' : 'Akten'}
              {(activeFilters > 0 || searchQuery) && ' (gefiltert)'}
            </span>
            <span className={`text-[9px] font-medium ${FEATURE_STATUS.aktiv.colorClass}`}>
              {FEATURE_STATUS.aktiv.label}
            </span>
          </div>
        </div>
      ) : (
        /* ============ KANBAN-ANSICHT ============ */
        <div className="flex gap-2 sm:gap-3 overflow-x-auto pb-4 snap-x snap-mandatory" style={{ minHeight: '360px' }}>
          {KANBAN_COLUMNS.map(col => {
            const columnAkten = filteredAkten.filter(a => a.status === col.status);
            return (
              <div key={col.status} className="flex-shrink-0 w-[75vw] sm:w-[280px] snap-start">
                {/* Column Header */}
                <div className="flex items-center gap-2 mb-3 px-1">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: col.color }} />
                  <span className="text-xs font-semibold text-gray-700">{col.label}</span>
                  <span className="text-[10px] font-medium text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full">
                    {columnAkten.length}
                  </span>
                </div>
                {/* Cards */}
                <div className="space-y-2">
                  {columnAkten.length === 0 ? (
                    <div className="border-2 border-dashed border-gray-100 rounded-lg py-8 text-center">
                      <p className="text-[10px] text-gray-300">Keine Akten</p>
                    </div>
                  ) : (
                    columnAkten.map(akte => (
                      <KanbanKarte
                        key={akte.id}
                        akte={akte}
                        onClick={() => handleOpenAkte(akte)}
                        betriebColor={betriebColor}
                        thumbnailUrl={thumbnailUrls[akte.id]}
                        onOpenForderungsDrawer={handleOpenForderungsDrawer}
                        onOpenFristModal={handleOpenFristModal}
                      />
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Neue Akte Modal */}
      <NeueAkteModal
        open={showNeueAkte}
        onClose={() => {
          setShowNeueAkte(false);
          setPrefillKunde('');
          setPrefillSchadennummer('');
          setPrefillSchadendatum('');
        }}
        betriebId={betriebId}
        betriebColor={betriebColor}
        prefillKunde={prefillKunde}
        prefillSchadennummer={prefillSchadennummer}
        prefillSchadendatum={prefillSchadendatum}
      />

      {/* Forderungsmanager Drawer */}
      <ForderungsmanagerDrawer
        open={forderungsDrawerOpen}
        onOpenChange={(open) => {
          setForderungsDrawerOpen(open);
          if (!open) setForderungsDrawerAkteId(undefined);
        }}
        akteId={forderungsDrawerAkteId}
      />

      {/* Frist setzen Modal */}
      <FristSetzenModal
        open={fristModalOpen}
        onClose={() => { setFristModalOpen(false); setFristModalAkteId(''); setFristModalAktenNummer(null); }}
        akteId={fristModalAkteId}
        aktenNummer={fristModalAktenNummer}
      />
    </div>
  );
}
